import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_CATEGORY,
  UPDATE_CATEGORY,
  DELETE_CATEGORY,
  GET_ALL_CATEGORIES
} from '../graphql/categoryOperations';
import {
  TrashIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/solid';
import { handleGraphQLError, formatDate } from '../utils/errorHandler';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';

const CategoriesManagement = () => {
  // Theme hook
  const { isDarkMode } = useTheme();

  const [categoryName, setCategoryName] = useState('');
  const [categoryImage, setCategoryImage] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  // Fetch all categories
  const { loading, error, data, refetch } = useQuery(GET_ALL_CATEGORIES, {
    onError: (error) => handleGraphQLError(error, 'Failed to load categories')
  });

  // Mutations
  const [createCategory] = useMutation(CREATE_CATEGORY, {
    onCompleted: () => {
      setCategoryName('');
      setIsCreateModalOpen(false);
      refetch();
      toast.success('Category created successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to create category')
  });

  const [updateCategory] = useMutation(UPDATE_CATEGORY, {
    onCompleted: () => {
      setEditingCategory(null);
      setCategoryName('');
      refetch();
      toast.success('Category updated successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to update category')
  });

  const [deleteCategory] = useMutation(DELETE_CATEGORY, {
    onCompleted: () => {
      refetch();
      toast.success('Category deleted successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to delete category')
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImagePreview(URL.createObjectURL(file));
      setCategoryImage(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) {
      toast.error('Category name cannot be empty');
      return;
    }

    try {
      // First, create or update the category to get an ID
      let categoryId;
      let imageUrl = editingCategory ? editingCategory.image : null;

      if (editingCategory) {
        // If editing, update the category first
        await updateCategory({
          variables: {
            updateCategoryId: editingCategory.id,
            input: {
              name: categoryName.trim()
            }
          }
        });
        categoryId = editingCategory.id;
      } else {
        // If creating, create the category first
        const result = await createCategory({
          variables: {
            input: {
              name: categoryName.trim()
            }
          }
        });
        categoryId = result.data.createCategory.id;
      }

      // Then handle the image upload if there's a new image
      if (categoryImage) {
        const formData = new FormData();
        formData.append('image', categoryImage);
        formData.append('categoryId', categoryId);

        // Upload the image
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const uploadResponse = await axios.post(`${baseUrl}/category/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        imageUrl = uploadResponse.data.imageUrl;

        // Update the category with the new image URL if this was a new category
        if (!editingCategory) {
          await updateCategory({
            variables: {
              updateCategoryId: categoryId,
              input: { image: imageUrl }
            }
          });
        }
      }
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category. Please try again.');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setCategoryImage(category.image || '');
    setImagePreview(category.image || '');
  };

  const handleDelete = (categoryId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this category?');
    if (confirmDelete) {
      deleteCategory({
        variables: { deleteCategoryId: categoryId }
      });
    }
  };

  const openCreateModal = () => {
    setCategoryName('');
    setCategoryImage('');
    setImagePreview('');
    setIsCreateModalOpen(true);
    setCategoryName('');
    setEditingCategory(null);
  };

  const closeModal = () => {
    setIsCreateModalOpen(false);
    setEditingCategory(null);
    setCategoryName('');
    setCategoryImage('');
    setImagePreview('');
  };

  // Loading and error states
  if (loading) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
      Loading categories...
    </div>
  );

  if (error) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>
      Error loading categories
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Create New Category Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={openCreateModal}
          className={`
            flex items-center px-4 py-2 rounded-lg text-white font-semibold
            transition-all duration-300
            ${isDarkMode
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-blue-500 hover:bg-blue-600'
            }
          `}
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create New Category
        </button>
      </div>

      {/* Create/Edit Category Modal */}
      {(isCreateModalOpen || editingCategory) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            className={`
              w-full max-w-md 
              ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
              rounded-2xl shadow-2xl border
              transition-colors duration-300
              ${editingCategory
                ? 'ring-4 ring-blue-500/20'
                : 'ring-4 ring-green-500/20'
              }
            `}
          >
            <div className={`
              px-6 py-4 
              ${isDarkMode ? 'border-b-gray-700' : 'border-b-gray-200'}
              border-b 
              flex justify-between items-center
            `}>
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {editingCategory ? 'Edit Category' : 'Create New Category'}
              </h2>
              <button
                onClick={closeModal}
                className={`
                  ${isDarkMode
                    ? 'text-gray-400 hover:text-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                  } 
                  transition
                `}
                title="Cancel"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label htmlFor="categoryName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category Name
                </label>
                <input
                  type="text"
                  id="categoryName"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter category name"
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="categoryImage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category Image
                </label>
                <input
                  type="file"
                  id="categoryImage"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
                {imagePreview && (
                  <div className="mt-2">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-20 w-20 object-cover rounded-md"
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className={`
                    px-6 py-2.5 rounded-lg
                    ${isDarkMode
                      ? 'text-gray-300 bg-gray-700 hover:bg-gray-600'
                      : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                    }
                    transition-colors duration-300
                  `}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`
                    inline-flex items-center justify-center 
                    px-6 py-2.5 rounded-lg text-white font-semibold
                    transition-all duration-300
                    ${editingCategory
                      ? (isDarkMode
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-green-500 hover:bg-green-600')
                      : (isDarkMode
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-blue-500 hover:bg-blue-600')
                    }
                  `}
                >
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Categories List Card */}
      <div className={`
        ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
        shadow-lg rounded-xl border 
        transition-colors duration-300
      `}>
        {/* Card Header */}
        <div className={`
          px-6 py-4 
          ${isDarkMode ? 'border-b-gray-700' : 'border-b-gray-200'}
          border-b 
          flex justify-between items-center
        `}>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            Categories List
          </h2>
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Total: {data?.getAllCategories?.length || 0} categories
          </div>
        </div>

        {/* Categories Table */}
        <div className="overflow-x-auto">
          {data.getAllCategories.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No categories found
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className={`
                  ${isDarkMode
                    ? 'bg-gray-700 text-gray-300'
                    : 'bg-gray-100 text-gray-600'
                  } 
                  text-sm
                `}>
                  <th className="px-6 py-3 text-left">Name</th>
                  <th className="px-6 py-3 text-left">Created At</th>
                  <th className="px-6 py-3 text-left">Updated At</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.getAllCategories.map((category) => (
                  <tr
                    key={category.id}
                    className={`
                      border-b 
                      ${isDarkMode
                        ? 'border-gray-700 hover:bg-gray-700'
                        : 'border-gray-200 hover:bg-gray-50'
                      }
                      transition duration-150
                    `}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {category.image && (
                          <img
                            src={category.image}
                            alt={category.name}
                            className="h-10 w-10 rounded-full object-cover mr-3"
                          />
                        )}
                        <span>{category.name}</span>
                      </div>
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(category.createdAt)}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(category.updatedAt)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(category)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode
                              ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-700'
                              : 'text-blue-500 hover:text-blue-700 hover:bg-blue-100'
                            }
                            transition
                          `}
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(category.id)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-700'
                              : 'text-red-500 hover:text-red-700 hover:bg-red-100'
                            }
                            transition
                          `}
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoriesManagement;
